import { useEffect } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Constants
import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";

// Selectors
import * as userSelectors from "redux/user/selectors";
import * as authSelectors from "redux/auth/selectors";

// Utils
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "utils/localStorage";

export const FIRST_LOGIN_TIME_AFTER_REGISTRATION_KEY =
  "firstLoginTimeAfterRegistration";
export const SECOND_LOGIN_TIME_AFTER_REGISTRATION_KEY =
  "secondLoginTimeAfterRegistration";
export const THIRD_LOGIN_TIME_AFTER_REGISTRATION_KEY =
  "thirdLoginTimeAfterRegistration";
const EXCLUDED_SECTIONS = ["sportsbook", "game", "deposit", "slots"];

export const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * Хук для отображения попапа сбора номера телефона
 * Показывает попап при выполнении всех условий:
 * - У пользователя нет номера телефона
 * - Пользователь сделал первый депозит
 * - Прошло не менее 5 минут с момента первого входа после регистрации
 * - Пользователь не находится в игре или кассе
 * - Попап показывался не более 2 раз
 * - Прошло не менее 24 часов с последнего показа
 */

export const useCollectPhonePopupTrigger = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { phoneNumber, firstDeposit } = useSelector(userSelectors.all);
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const phonePopupStatus = getLocalStorageItem("phoneTriggerStatus");

  useEffect(() => {
    const phoneTriggerStatus = getLocalStorageItem("phoneTriggerStatus");

    if (!phoneTriggerStatus) {
      setLocalStorageItem("phoneTriggerStatus", "1");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const phoneTriggerStatus = getLocalStorageItem("phoneTriggerStatus");

    if (phoneTriggerStatus === "finished") {
      return;
    }

    if (phoneNumber) {
      // eslint-disable-next-line no-console
      console.log("У пользователя есть номер телефона:", phoneNumber);
      setLocalStorageItem("phoneTriggerStatus", "finished");
      return;
    }

    if (phoneNumber && !isAuthenticated) {
      return;
    }

    if (!firstDeposit) {
      return;
    }

    if (phoneTriggerStatus === "1") {
      // eslint-disable-next-line no-console
      console.log("==== БЛОК 1 ====", phoneTriggerStatus);

      const firstLoginTime = (Date.now() + FIVE_MINUTES_MS).toString();

      setLocalStorageItem(
        FIRST_LOGIN_TIME_AFTER_REGISTRATION_KEY,
        firstLoginTime,
      );

      const intervalId = setInterval(() => {
        const currentTime = Date.now();
        const targetTime = parseInt(firstLoginTime, 10);

        if (currentTime >= targetTime) {
          const isExcludedSection = EXCLUDED_SECTIONS.some(
            (path) =>
              location.search.includes(path) ||
              location.pathname.includes(path),
          );

          if (isExcludedSection) {
            return;
          }

          navigate({
            search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.collectPhone })}`,
          });

          removeLocalStorageItem(FIRST_LOGIN_TIME_AFTER_REGISTRATION_KEY);

          clearInterval(intervalId);
        }
      }, 20000);

      return () => {
        clearInterval(intervalId);
      };
    }

    if (phoneTriggerStatus === "2") {
      // eslint-disable-next-line no-console
      console.log("==== БЛОК 2 ====", phoneTriggerStatus);

      const secondLoginPopupTime = getLocalStorageItem(
        SECOND_LOGIN_TIME_AFTER_REGISTRATION_KEY,
      );

      if (!secondLoginPopupTime) {
        setLocalStorageItem(
          SECOND_LOGIN_TIME_AFTER_REGISTRATION_KEY,
          (Date.now() + TWENTY_FOUR_HOURS_MS).toString(),
        );
      }

      const currentTime = Date.now();
      const targetTime = parseInt(secondLoginPopupTime, 10);

      if (currentTime >= targetTime) {
        const secondLoginTime = (Date.now() + FIVE_MINUTES_MS).toString();

        setLocalStorageItem(
          THIRD_LOGIN_TIME_AFTER_REGISTRATION_KEY,
          secondLoginTime,
        );

        const secondIntervalId = setInterval(() => {
          const nowTime = Date.now();
          const secondTargetTime = parseInt(secondLoginTime, 10);

          if (nowTime >= secondTargetTime) {
            const isExcludeSection = EXCLUDED_SECTIONS.some(
              (path) =>
                location.search.includes(path) ||
                location.pathname.includes(path),
            );

            if (isExcludeSection) {
              return;
            }

            navigate({
              search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.collectPhone })}`,
            });

            removeLocalStorageItem(THIRD_LOGIN_TIME_AFTER_REGISTRATION_KEY);

            setLocalStorageItem("phoneTriggerStatus", "finished");
          }
        }, 20000);

        return () => {
          clearInterval(secondIntervalId);
        };
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phoneNumber,
    firstDeposit,
    isAuthenticated,
    phonePopupStatus,
    location?.search,
    location?.pathname,
  ]);
};
